import * as Dates from '../Dates';

/** "Sat Oct 24" or "Sat 24 Oct": the weekday, then day and month in order. */
const weekdayDayMonth = () => `ddd ${Dates.dayMonthFormat()}`;

/**
 * Format a forecast-map timestamp (run time / frame valid time) for display.
 *
 * The manifest's `time` and `run_datetime` arrive from the API as Unix epoch
 * SECONDS (the backend's DRF `DATETIME_FORMAT="%s"`), not ISO strings — despite
 * the field name and the `string` type. The previous code did
 * `Dates.formatDate(moment(value).unix(), …)`, and `moment()` reads the epoch
 * number as MILLISECONDS, landing on 1970-01-21 (~16:24) — a garbage date the
 * year-less `ddd D MMM` format silently hid.
 *
 * Treat the value as seconds and go straight through the core Dates helper so
 * it honours the user's date and time preferences, timezone and moment
 * locale: "Sat Oct 24, 4:00 PM" (English, month-first, 12-hour), "Sat 24 Oct,
 * 16:00" (day-first, 24-hour), "sam. 24 oct., 16:00". The day and month come
 * in the user's order through the moment locale (`Dates.dayMonthFormat`,
 * FE #428/#430), the time on the user's clock, unpadded (`Dates.clockFormat`):
 * a hard-coded `ddd D MMM` and `hh:mm A` gave a US user "Sat 24 Oct, 04:00 PM"
 * (FE #439).
 */
export const formatForecastTimeLabel = (
  value: string | number,
  timezone: string,
  dateTimeConfig: Dates.DateTimeConfig,
  // Moment format. Defaults to the weekday, day and month, and the time.
  format = `${weekdayDayMonth()}, ${Dates.clockFormat(dateTimeConfig)}`,
): string => {
  const seconds = Number(value);
  if (!Number.isFinite(seconds)) {
    return '-';
  }
  return Dates.formatDate(seconds, timezone, format);
};

/**
 * The weekday and time in UTC, "Sat 20:00" / "Sat 8:00 PM", beneath the
 * local time: the UTC day can differ from the local one. The map timeline in
 * both apps and the webapp flipbook.
 */
export const formatForecastUtcTimeLabel = (
  value: string | number,
  dateTimeConfig: Dates.DateTimeConfig,
): string =>
  formatForecastTimeLabel(
    value,
    'UTC',
    dateTimeConfig,
    `ddd ${Dates.clockFormat(dateTimeConfig)}`,
  );

/**
 * A run named by its UTC init time, "Mon Oct 12, 18 UTC" / "Mon 12 Oct, 18
 * UTC" in the user's date order: runs are 00z/06z/… whatever the device zone,
 * and are known by that 24-hour UTC hour on either clock. The forecast map's
 * "Run: {run}" in both apps.
 */
export const formatForecastRunLabel = (
  runDatetime: string | number,
  dateTimeConfig: Dates.DateTimeConfig,
): string =>
  `${formatForecastTimeLabel(runDatetime, 'UTC', dateTimeConfig, `${weekdayDayMonth()}, HH`)} UTC`;

export default formatForecastTimeLabel;
